<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import type { PnMultiselectOption } from "@postnord/web-components/types";
import { storeToRefs } from "pinia";
import { userStickers } from "@/stores/stickers";
import { useTagManagementStore } from "@/stores/tagManagement";
import type { ApiTagCategory } from "@/types/tagManagement";
import constant from "@/constant";

const stickerStore = userStickers();
const { createCollectionReqestParams } = storeToRefs(stickerStore);
const tagManagementStore = useTagManagementStore();

const dropdownSelections = ref<Record<string, PnMultiselectOption[]>>({});
const isLoading = ref(false);
const validated = ref(false);

const themeGroups = computed<ApiTagCategory[]>(() =>
  tagManagementStore.getThemeCategories(constant.TAG_THEME_IDS.THEME),
);

const getEnglishLabel = (group: ApiTagCategory): string =>
  `${group.enCategoryName}`;

const hasAnyThemeSelection = (): boolean => {
  const themeSelections =
    createCollectionReqestParams.value.themeSelections ?? {};
  return Object.values(themeSelections).some((ids) => ids.length > 0);
};

const validate = (): boolean => {
  validated.value = true;
  return hasAnyThemeSelection();
};

defineExpose({ validate });

const buildOptions = (group: ApiTagCategory): PnMultiselectOption[] =>
  (group.children ?? []).map((child) => {
    const grandchildren = child.children ?? [];
    if (grandchildren.length > 0) {
      return {
        label: child.enCategoryName,
        value: String(child.categoryId),
        checked: false,
        group: grandchildren.map((grandchild) => ({
          label: grandchild.enCategoryName,
          value: String(grandchild.categoryId),
          checked: false,
        })),
      };
    }
    return {
      label: child.enCategoryName,
      value: String(child.categoryId),
      checked: false,
    };
  });

const getOptions = (group: ApiTagCategory): PnMultiselectOption[] =>
  dropdownSelections.value[group.categoryId] ?? [];

const collectCheckedIds = (options: PnMultiselectOption[]): string[] =>
  options.flatMap((option) => {
    const grandchildIds = option.group ? collectCheckedIds(option.group) : [];
    return option.checked
      ? [String(option.value), ...grandchildIds]
      : grandchildIds;
  });

const onSelectionChange = (
  groupId: number,
  event: CustomEvent<PnMultiselectOption[]>,
) => {
  dropdownSelections.value = {
    ...dropdownSelections.value,
    [groupId]: [...event.detail],
  };
  stickerStore.setThemeSelections({
    ...(createCollectionReqestParams.value.themeSelections ?? {}),
    [groupId]: collectCheckedIds(event.detail),
  });
  stickerStore.syncCategoriesFromSelections();
};

onMounted(async () => {
  isLoading.value = true;
  if (!tagManagementStore.tagTree) {
    await tagManagementStore.getTagTree();
  }
  const groups = tagManagementStore.getThemeCategories(
    constant.TAG_THEME_IDS.THEME,
  );
  const storedSelections =
    createCollectionReqestParams.value.themeSelections ?? {};
  const initialSelections: Record<string, PnMultiselectOption[]> = {};
  groups.forEach((group) => {
    const storedGroupIds = storedSelections[group.categoryId] ?? [];
    initialSelections[group.categoryId] = buildOptions(group).map((option) => ({
      ...option,
      checked: storedGroupIds.includes(String(option.value)),
      group: option.group?.map((grandchild) => ({
        ...grandchild,
        checked: storedGroupIds.includes(String(grandchild.value)),
      })),
    }));
  });
  dropdownSelections.value = initialSelections;
  isLoading.value = false;
});
</script>

<template>
  <section>
    <div class="theme-selection">
      <h3 class="pn-m">What best describes the theme of these stickers?</h3>
      <p class="pn-s width-67">
        Is the collection created for a specific occasion (e.g. Christmas or
        birthdays), to communicate a feeling (e.g. "love" or "feel better") or
        is there another way to sum up the overall theme?
      </p>
      <div class="sticker-list">
        <div
          class="sticker-item"
          v-for="sticker in createCollectionReqestParams.stickers"
          :key="sticker.filename"
        >
          <img
            class="thumb"
            :src="`data:image/svg+xml;base64,${sticker.base64}`"
            :alt="sticker.filename"
          />
        </div>
      </div>
      <div :class="[{ spinner: isLoading }]">
        <pn-spinner size="3" v-show="isLoading" />
      </div>
      <div v-if="!isLoading" class="dropdowns">
        <pn-multiselect
          v-for="group in themeGroups"
          :key="group.categoryId"
          :label="getEnglishLabel(group)"
          :options="getOptions(group)"
          placeholder="Select if applicable"
          @allOptions="onSelectionChange(group.categoryId, $event)"
        ></pn-multiselect>
      </div>
      <span
        class="critical pn-s error"
        v-if="validated && !hasAnyThemeSelection()"
        >You need to make a selection from at least one of the categories</span
      >
    </div>
  </section>
</template>

<style lang="scss" scoped>
.theme-selection {
  display: flex;
  flex-direction: column;
  padding-left: 32px;
}
.width-67 {
  width: 67%;
}
.sticker-list {
  display: grid;
  grid-template-columns: repeat(13, 1fr);
  gap: 10px;
  padding: 8px 0;
}
.sticker-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  height: 60px;
}
.thumb {
  width: 100%;
  height: 60px;
  object-fit: contain;
}
.dropdowns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  align-items: end;
  gap: 16px;
  padding-top: 15px;
}

.error {
  margin-top: 8px;
}
</style>
